import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Spin } from "antd";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CloudOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { useAppSelector } from "../../../store/hooks";
import { useIsEdgeLocal } from "../../../hooks/useEdgeMode";
import type { SchemaElement } from "../../../api/restaurantSchema";
import { useRestaurantSchema } from "../../../hooks/useRestaurantSchema";
import { compareTableNames } from "../../../components/restaurant-schema/tableNames";
import type { ModuleOutletContext } from "../../../components/moduleSidebar/ModuleSidebarLayout";
import DeleteConfirmModal from "../../configuration/components/DeleteConfirmModal";
import RegionCreateModal from "../restaurant-schema/modals/RegionCreateModal";
import BulkEditPanel from "./BulkEditPanel";
import NewTablesPanel from "./NewTablesPanel";
import TableEditPanel from "./TableEditPanel";
import TablesConfigHeader from "./TablesConfigHeader";
import TablesConfigTopBar from "./TablesConfigTopBar";
import TablesGridCard from "./TablesGridCard";
import { SelectionBar, TablesBottomSheet } from "./TablesMobileSheet";
import { useTablesConfigActions } from "./useTablesConfigActions";
import { useTablesSelection } from "./useTablesSelection";

// Кой панел е отворен като bottom sheet на телефон.
type MobileSheet = "new" | "selection" | null;

/**
 * Резервации → Конфигурация на маси: масите като плочки по региони — без
 * схема. Вляво мрежата (търсене, филтър по места, множествен избор), вдясно
 * панелът: „Нови маси" (една / серия), редакция на една маса или групови
 * действия. Таблет: панелът е по-тясна колона от 768px (под това — над
 * мрежата). Телефон: панелът е bottom sheet, изборът — лента долу. Новата
 * маса се нарежда сама в региона (autoLayout.ts). Регион се създава ръчно —
 * без регион няма къде да отиде маса.
 */
const TablesConfigPage = () => {
  const { t } = useTranslation();
  const { isNarrow, onOpenMobileMenu } = useOutletContext<ModuleOutletContext>();
  const permissions = useAppSelector((state) => state.auth.permissions);
  const canOpenSchema = permissions.some((permission) => permission.name === "manage_restaurant_schema");

  // Без интернет заявките отиват към касата, а тя няма схемата (503) —
  // вместо сива грешка казваме защо (docs/edge.md: disabled с подсказка).
  const isEdgeLocal = useIsEdgeLocal();
  const { data: regions = [], isLoading, isError } = useRestaurantSchema();
  const actions = useTablesConfigActions();
  const [activeRegionId, setActiveRegionId] = useState<number | null>(null);
  const [isCreatingRegion, setIsCreatingRegion] = useState(false);
  const [tablesToDelete, setTablesToDelete] = useState<SchemaElement[] | null>(null);
  const [search, setSearch] = useState("");
  // Филтърът по места помни за кой регион е избран — ако регионът изчезне
  // (изтрит другаде) и страницата падне на първия, филтърът не се пренася.
  const [seatFilter, setSeatFilter] = useState<{ regionId: number; seats: number } | null>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  const [mobileSheet, setMobileSheet] = useState<MobileSheet>(null);
  // Груповата операция (места/местене) върви маса по маса: докато тече,
  // панелът работи със снимката на избора отпреди старта, а изборът е
  // замразен — иначе преместените маси изпадат, панелът се сменя и бутоните
  // (вкл. „Изтрий") се връщат активни по средата на цикъла.
  const [bulkRun, setBulkRun] = useState<SchemaElement[] | null>(null);
  const isBulkWorking = bulkRun !== null;

  const region = useMemo(
    () => regions.find((candidate) => candidate.id === activeRegionId) ?? regions[0],
    [regions, activeRegionId],
  );
  const tables = useMemo(
    () => (region?.elements ?? []).filter((element) => element.kind === "table").sort(compareTableNames),
    [region],
  );
  // Филтър по места от друг регион или чиято група вече я няма (изтрити/сменени
  // маси) — отпада.
  const activeSeatFilter =
    seatFilter !== null &&
    seatFilter.regionId === region?.id &&
    tables.some((table) => table.seats === seatFilter.seats)
      ? seatFilter.seats
      : null;
  const searchKey = search.trim().toLocaleLowerCase();
  // Изборът е върху масите на региона; смяна на чипа/търсенето го чисти, така
  // че се избират само видими маси.
  const selection = useTablesSelection(tables);
  const selectedCount = selection.selectedTables.length;
  // Вече избраната маса остава видима, докато е избрана — иначе смяна на
  // местата (или номера) я скрива от филтъра и панелът ѝ се затваря.
  const visibleTables = useMemo(
    () =>
      tables.filter(
        (table) =>
          selection.selectedIds.has(table.id) ||
          ((activeSeatFilter === null || table.seats === activeSeatFilter) &&
            (!searchKey || (table.name ?? "").toLocaleLowerCase().includes(searchKey))),
      ),
    [tables, activeSeatFilter, searchKey, selection.selectedIds],
  );
  const panelTables = bulkRun ?? selection.selectedTables;

  // Изборът изчезна (изтрити/преместени маси) → sheet-ът за него се затваря.
  useEffect(() => {
    if (mobileSheet === "selection" && selectedCount === 0 && !isBulkWorking) setMobileSheet(null);
  }, [mobileSheet, selectedCount, isBulkWorking]);

  // Фокусът от „+ Нова маса" е еднократен — след избор панелът „Нови маси"
  // се връща без да краде фокуса.
  useEffect(() => {
    if (selectedCount > 0) setFocusRequest(0);
  }, [selectedCount]);

  // Телефонът стана широк → панелът е в колоната, sheet-ът не трябва.
  useEffect(() => {
    if (!isNarrow) setMobileSheet(null);
  }, [isNarrow]);

  const closeSheet = () => {
    if (isBulkWorking) return;
    // Затворена редакция на една маса = край на избора (иначе остава лентата).
    if (mobileSheet === "selection" && selectedCount === 1) selection.clear();
    setMobileSheet(null);
  };

  // Esc: затваря sheet-а, после отказва избора. Без избор не се пипа —
  // тогава е глобалното „назад". Полетата, които връщат стойност с Esc,
  // викат preventDefault, затова тук се пропускат.
  const isModalOpen = isCreatingRegion || tablesToDelete !== null;
  const hasEscapeTarget = mobileSheet !== null || selectedCount > 0;
  useEffect(() => {
    if (isModalOpen || isBulkWorking || !hasEscapeTarget) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      if (mobileSheet !== null) closeSheet();
      else selection.clear();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  // Нов филтър/търсене = нов избор: иначе скритите маси се връщат избрани.
  const changeSeatFilter = (seats: number | null) => {
    if (isBulkWorking) return;
    setSeatFilter(seats === null || !region ? null : { regionId: region.id, seats });
    selection.clear();
  };

  const changeSearch = (value: string) => {
    if (isBulkWorking) return;
    setSearch(value);
    selection.clear();
  };

  const changeRegion = (regionId: number) => {
    if (isBulkWorking) return;
    setActiveRegionId(regionId);
    setSeatFilter(null);
    selection.clear();
    setMobileSheet(null);
  };

  const openNewTables = () => {
    if (isBulkWorking) return;
    selection.clear();
    setFocusRequest((current) => current + 1);
    if (isNarrow) setMobileSheet("new");
  };

  const selectTable = (table: SchemaElement) => {
    if (isBulkWorking) return;
    if (isNarrow) {
      selection.selectMany([table]);
      setMobileSheet("selection");
    } else {
      selection.selectOnly(table);
    }
  };

  const notice = (tone: "warn" | "danger", icon: ReactNode, title: string, hint: string) => (
    <div className="flex items-start gap-3 rounded-[14px] border border-shade-2 bg-surface px-6 py-5">
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] ${
          tone === "warn" ? "bg-warn-tint text-warn-ink" : "bg-danger-tint text-danger-ink"
        }`}
      >
        {icon}
      </span>
      <div>
        <div className="text-[14px] font-bold tracking-[-0.2px] text-ink">{title}</div>
        <div className="mt-0.5 text-[12.5px] text-body">{hint}</div>
      </div>
    </div>
  );

  const runBulk = async (work: () => Promise<unknown>) => {
    setBulkRun(selection.selectedTables);
    try {
      await work();
    } finally {
      setBulkRun(null);
    }
  };

  const renderPanel = (inSheet: boolean) => {
    if (!region) return null;
    if (!isBulkWorking && selectedCount === 1) {
      const [table] = selection.selectedTables;
      return (
        <TableEditPanel
          key={table.id}
          table={table}
          regions={regions}
          actions={actions}
          onDelete={() => setTablesToDelete([table])}
          onClose={() => {
            selection.clear();
            setMobileSheet(null);
          }}
        />
      );
    }
    if (panelTables.length > 1 || isBulkWorking) {
      return (
        <BulkEditPanel
          key={panelTables.map((table) => table.id).join(",")}
          tables={panelTables}
          region={region}
          regions={regions}
          actions={actions}
          isWorking={isBulkWorking}
          onRun={(work) => void runBulk(work)}
          onDelete={() => setTablesToDelete(selection.selectedTables)}
          onClearSelection={() => {
            selection.clear();
            setMobileSheet(null);
          }}
        />
      );
    }
    return (
      <NewTablesPanel
        region={region}
        regions={regions}
        actions={actions}
        canOpenSchema={canOpenSchema}
        focusRequest={focusRequest}
        onClose={inSheet ? closeSheet : undefined}
      />
    );
  };

  const renderBody = () => {
    if (isEdgeLocal) {
      return notice("warn", <CloudOutlined />, t("edge.unavailableOffline"), t("tablesConfig.offlineHint"));
    }
    if (isLoading) {
      return (
        <div className="flex justify-center rounded-[14px] border border-shade-2 bg-surface p-12">
          <Spin size="large" />
        </div>
      );
    }
    if (isError) {
      return notice(
        "danger",
        <ExclamationCircleOutlined />,
        t("restaurantSchema.loadError"),
        t("restaurantSchema.loadErrorHint"),
      );
    }
    // Без регион масата няма къде да отиде — първо зала (създава се ръчно).
    if (!region) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 rounded-[14px] border border-dashed border-shade-4 bg-surface px-6 py-14 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-[18px] bg-brand-tint text-[30px] text-brand-deep-ink">
            <TableOutlined />
          </span>
          <div>
            <div className="text-[20px] font-bold leading-[1.2] tracking-[-0.4px] text-ink">
              {t("tablesConfig.empty.title")}
            </div>
            <div className="mx-auto mt-2 max-w-[440px] text-[13.5px] font-medium leading-[1.5] text-body">
              {t("tablesConfig.empty.description")}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsCreatingRegion(true)}
            className="flex h-[42px] cursor-pointer items-center gap-2 rounded-[10px] border-none bg-brand px-5 text-[14px] font-bold text-white transition-colors duration-150 hover:bg-brand-deep"
          >
            <PlusOutlined />
            {t("tablesConfig.empty.cta")}
          </button>
        </div>
      );
    }

    const grid = (
      <TablesGridCard
        region={region}
        tables={tables}
        visibleTables={visibleTables}
        selectedIds={selection.selectedIds}
        seatFilter={activeSeatFilter}
        isNarrow={isNarrow}
        hasSearch={searchKey.length > 0}
        onSeatFilterChange={changeSeatFilter}
        onSelect={selectTable}
        onToggle={(table) => !isBulkWorking && selection.toggle(table)}
        onSelectAll={() => !isBulkWorking && selection.selectMany(visibleTables)}
        onClearSelection={() => !isBulkWorking && selection.clear()}
        onAddTable={openNewTables}
      />
    );

    return (
      <>
        <TablesConfigHeader
          regions={regions}
          activeRegionId={region.id}
          search={search}
          isNarrow={isNarrow}
          onRegionChange={changeRegion}
          onCreateRegion={() => setIsCreatingRegion(true)}
          onSearchChange={changeSearch}
        />
        {isNarrow ? (
          <>
            <button
              type="button"
              onClick={openNewTables}
              className="flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-[12px] border-none bg-brand text-[14px] font-bold text-white transition-colors duration-150 hover:bg-brand-deep"
            >
              <PlusOutlined className="text-[13px]" />
              {t("tablesConfig.newTables.kicker")}
            </button>
            {grid}
          </>
        ) : (
          <div className="grid items-start gap-4 md:grid-cols-[minmax(0,1fr)_288px] xl:grid-cols-[minmax(0,1fr)_340px]">
            {grid}
            {/* Под 768px панелът е над мрежата; от 768px — лепкава колона вдясно. */}
            <aside className="order-first rounded-[16px] border border-shade-2 bg-surface px-5 py-5 md:sticky md:top-4 md:order-none">
              {renderPanel(false)}
            </aside>
          </div>
        )}
      </>
    );
  };

  const showSelectionBar = isNarrow && selectedCount > 0 && mobileSheet === null;

  return (
    <div className="flex min-h-full flex-col bg-canvas">
      <TablesConfigTopBar isNarrow={isNarrow} onOpenMobileMenu={onOpenMobileMenu} />
      <div
        className={`flex flex-1 flex-col gap-4 px-3.5 pt-3.5 sm:px-7 sm:pb-8 sm:pt-4 ${
          showSelectionBar ? "pb-28" : "pb-6"
        }`}
      >
        {renderBody()}
      </div>

      {showSelectionBar && (
        <SelectionBar
          count={selectedCount}
          onEdit={() => setMobileSheet("selection")}
          onClear={() => !isBulkWorking && selection.clear()}
        />
      )}
      {isNarrow && mobileSheet !== null && region && (
        <TablesBottomSheet onClose={closeSheet}>{renderPanel(true)}</TablesBottomSheet>
      )}

      {isCreatingRegion && (
        <RegionCreateModal
          open
          onClose={() => setIsCreatingRegion(false)}
          onSubmit={async (name) => {
            const created = await actions.createRegion(name);
            changeRegion(created.id);
            setIsCreatingRegion(false);
          }}
        />
      )}

      <DeleteConfirmModal
        open={tablesToDelete !== null}
        title={
          tablesToDelete && tablesToDelete.length > 1
            ? t("tablesConfig.delete.titleMany", { count: tablesToDelete.length })
            : t("tablesConfig.delete.title", { name: tablesToDelete?.[0]?.name ?? "" })
        }
        description={
          tablesToDelete && tablesToDelete.length > 1
            ? t("tablesConfig.delete.descriptionMany", {
                names: tablesToDelete.map((table) => table.name).join(", "),
              })
            : t("tablesConfig.delete.description")
        }
        confirmLabel={t("tablesConfig.delete.confirm")}
        cancelLabel={t("common.cancel")}
        icon={<DeleteOutlined className="text-[18px]" />}
        onConfirm={async () => {
          if (!tablesToDelete) return;
          // Отказаните (резервация/отворена сметка) остават избрани — съобщението казва кои.
          const deletedIds = await actions.deleteTables(tablesToDelete);
          selection.setSelectedIds((current) => {
            const next = new Set(current);
            deletedIds.forEach((id) => next.delete(id));
            return next;
          });
          setTablesToDelete(null);
        }}
        onCancel={() => setTablesToDelete(null)}
      />
    </div>
  );
};

export default TablesConfigPage;
